import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppContext } from '../context/AppContext';
import { useEnergyContext } from '../context/EnergyContext';
import { useAuth } from '../auth/AuthContext';
import { energyService } from '../api/energyApi';
import { createContextAwareApiClient } from '../api/contextAwareApiClient';
import InlineEditForm from './InlineEditForm';

function extractCachedCanopies(rawInventory, fleetId) {
  const list = Array.isArray(rawInventory?.data)
    ? rawInventory.data
    : (Array.isArray(rawInventory) ? rawInventory : []);
  if (!fleetId) return list;
  return list.filter((c) => !c?.fleetId || c.fleetId === fleetId);
}

/**
 * CanopySelector component provides a dropdown for selecting a canopy
 * from the currently selected fleet. When a canopy is selected,
 * it will display real-time telemetry data for that canopy.
 */
function CanopySelector() {
  const auth = useAuth();
  const canManage = auth.isSuperuser;
  const { 
    customerContext,
    fleetContext, 
    canopyContext, 
    setCanopy, 
    clearCanopy,
    getContextData,
    validateContext 
  } = useAppContext();
  const energyContext = useEnergyContext();
  const fleetId = fleetContext?.fleetId;
  const scopeRef = useRef({});
  scopeRef.current = auth.isSuperuser ? { customerId: customerContext?.customerId } : {};
  
  // Use ref to store the service to prevent infinite loops
  const canopyServiceRef = useRef();
  canopyServiceRef.current = createContextAwareApiClient(getContextData, validateContext).canopyService;
  const cachedCanopiesRef = useRef([]);
  cachedCanopiesRef.current = extractCachedCanopies(
    energyContext?.raw?.canopyInventory,
    fleetContext?.fleetId
  );
  
  const [canopies, setCanopies] = useState(() => cachedCanopiesRef.current);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const requestVersion = useRef(0);
  const invalidateRequests = useCallback(() => { ++requestVersion.current; }, []);

  // Canopy creation dialog states
  const [isEditing, setIsEditing] = useState(false);
  const [editingCanopy, setEditingCanopy] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Notification states
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Field configuration for canopy editing
  const canopyFields = [
    {
      name: 'canopyId',
      label: 'Canopy ID',
      required: true,
      disabled: !!editingCanopy?.canopyId, // Read-only for existing canopies
      helperText: 'Unique identifier for the canopy'
    },
    {
      name: 'canopyName',
      label: 'Canopy Name',
      required: false,
      helperText: 'A descriptive name for the canopy'
    },
    {
      name: 'canopyCommissioningDate',
      label: 'Commissioning Date',
      required: false,
      type: 'date',
      helperText: 'The date the canopy was commissioned'
    },
    {
      name: 'canopyData',
      label: 'Canopy Data',
      required: false,
      multiline: true,
      rows: 4,
      type: 'json',
      helperText: 'Additional JSON data for the canopy'
    }
  ];

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchCanopies = useCallback(async (fleetId, retryAttempt = 0) => {
    if (!fleetId) {
      console.log('🚫 CANOPY SELECTOR: No fleet ID provided');
      return;
    }

    console.log(`🔄 CANOPY SELECTOR: Starting canopy fetch (attempt ${retryAttempt + 1})`);
    console.log('📋 Fleet ID:', fleetId);
    
    const request = ++requestVersion.current;
    if (cachedCanopiesRef.current.length === 0) {
      setLoading(true);
    }
    setError(null);
    
    try {
      console.log('🌐 CANOPY SELECTOR: Calling API for fleet:', fleetId);
      const response = await energyService.getFleetCanopies(fleetId, scopeRef.current);
      if (request !== requestVersion.current) return;
      console.log('📡 CANOPY SELECTOR: API response received:', response);
      
      if (response.success && response.data) {
        // Handle the API response structure correctly
        // After transformation, the structure is: { status: "success", data: [...], metadata: {...} }
        const canopyData = response.data.data || (Array.isArray(response.data) ? response.data : []);
        console.log('📊 CANOPY SELECTOR: Canopy data extracted:', canopyData);
        console.log('🔢 CANOPY SELECTOR: Number of canopies:', canopyData.length);
        
        if (canopyData.length > 0) {
          console.log('✅ CANOPY SELECTOR: Canopies found, updating state');
          canopyData.forEach((canopy, index) => {
            console.log(`   Canopy ${index + 1}: ${canopy.canopyId} - ${canopy.canopyName || 'Unnamed'}`);
          });
        } else {
          console.log('⚠️ CANOPY SELECTOR: No canopies found for fleet');
        }
        
        setCanopies(canopyData);
        setRetryCount(0); // Reset retry count on success
      } else {
        const isTransientError = !response.status || response.status >= 500 || /timeout|network/i.test(response.message || '');
        if (isTransientError && retryAttempt < 2) {
          console.log(`🔄 CANOPY SELECTOR: Transient API error, retrying... (${retryAttempt + 1}/3)`);
          setRetryCount(retryAttempt + 1);
          setTimeout(() => {
            if (request === requestVersion.current) fetchCanopies(fleetId, retryAttempt + 1);
          }, 1000);
          return;
        }
        if (cachedCanopiesRef.current.length > 0) {
          setCanopies(cachedCanopiesRef.current);
          setError(null);
        } else {
          console.error('❌ CANOPY SELECTOR: Failed to load canopies:', response);
          setError(`Failed to load canopies: ${response.message || 'Unknown error'}`);
          setCanopies([]);
        }
      }
    } catch (err) {
      if (request !== requestVersion.current) return;
      console.error('💥 CANOPY SELECTOR: Error fetching canopies:', err);
      
      // Retry logic for network errors
      if ((err.message === 'Network Error' || /timeout|network/i.test(err.message || '')) && retryAttempt < 2) {
        console.log(`🔄 CANOPY SELECTOR: Network error, retrying... (${retryAttempt + 1}/3)`);
        setRetryCount(retryAttempt + 1);
        
        // Wait 1 second before retrying
        setTimeout(() => {
          if (request === requestVersion.current) fetchCanopies(fleetId, retryAttempt + 1);
        }, 1000);
        return;
      }
      
      if (cachedCanopiesRef.current.length > 0) {
        setCanopies(cachedCanopiesRef.current);
        setError(null);
      } else {
        setError(`Error: ${err.message}`);
        setCanopies([]);
      }
    } finally {
      if (request === requestVersion.current) setLoading(false);
      console.log('🏁 CANOPY SELECTOR: Canopy fetch completed');
    }
  }, []); // Remove canopyService dependency to prevent infinite loop

  useEffect(() => {
    const cached = extractCachedCanopies(energyContext?.raw?.canopyInventory, fleetContext?.fleetId);
    if (cached.length > 0) {
      setCanopies((prev) => (prev.length === 0 ? cached : prev));
      setError(null);
    }
  }, [energyContext?.raw?.canopyInventory, fleetContext?.fleetId]);

  // Fetch canopies when the fleet context changes
  useEffect(() => {
    invalidateRequests();
    setCanopies(cachedCanopiesRef.current);
    if (!fleetId) {
      console.log('🚫 CANOPY SELECTOR: No fleet context, clearing canopies');
      setCanopies([]);
      clearCanopy();
      setError(null);
      setRetryCount(0);
      return;
    }
    
    // Only fetch if we have a valid fleet ID
    fetchCanopies(fleetId);
    return invalidateRequests;
  }, [fleetId, fetchCanopies, clearCanopy, invalidateRequests]);

  const handleChange = (event) => {
    const canopyId = event.target.value;
    console.log('Canopy selection changed to:', canopyId);
    
    if (canopyId) {
      const selectedCanopy = canopies.find(c => c.canopyId === canopyId);
      console.log('Selected canopy:', selectedCanopy);
      setCanopy(selectedCanopy);
    } else {
      clearCanopy();
    }
  };

  const handleRetry = () => {
    if (fleetContext?.fleetId) {
      fetchCanopies(fleetContext.fleetId);
    }
  };

  const getCanopyDisplayName = (canopy) => {
    if (canopy.canopyName && canopy.canopyName.trim()) {
      return `${canopy.canopyName} (${canopy.canopyId})`;
    }
    return canopy.canopyId;
  };

  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  const handleAddCanopy = () => {
    if (!canManage) return;
    setEditingCanopy({
      canopyId: '',
      fleetId: fleetContext?.fleetId,
      canopyName: '',
      canopyCommissioningDate: '',
      canopyData: {}
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingCanopy(null);
  };

  const handleFinishedEdit = async () => {
    setIsEditing(false);
    setEditingCanopy(null);
    // Refresh the canopy list
    if (fleetContext?.fleetId) {
      fetchCanopies(fleetContext.fleetId);
    }
  };

  const handleSaveCanopy = async (formData) => {
    if (!canManage) return;
    setIsSubmitting(true);
    try {
      let response;
      const canopyPayload = { ...formData, fleetId: fleetContext?.fleetId };

      if (editingCanopy?.canopyId) {
        // Update existing canopy
        response = await canopyServiceRef.current.updateCanopy(editingCanopy.canopyId, canopyPayload);
      } else {
        // Create new canopy
        response = await canopyServiceRef.current.createCanopy(canopyPayload);
      }
      
      if (response.success) {
        showNotification(`Canopy ${editingCanopy?.canopyId ? 'updated' : 'created'} successfully`, 'success');
        handleFinishedEdit();
      } else {
        showNotification(`Failed to save canopy: ${response.message}`, 'error');
      }
    } catch (err) {
      showNotification(`An error occurred: ${err.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCanopy = (canopy) => {
    if (!canManage) return;
    setEditingCanopy({ ...canopy });
    setIsEditing(true);
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" component="div">
          Canopy Selection
        </Typography>
        <Chip 
          label={`${canopies.length} canopies`} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
        {canManage && <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={!fleetContext}
          onClick={handleAddCanopy}
        >
          New Canopy
        </Button>}
      </Stack>
      
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl fullWidth>
          <InputLabel id="canopy-selector-label">Canopy</InputLabel>
          <Select
            labelId="canopy-selector-label"
            value={canopyContext?.canopyId || ''}
            label="Canopy"
            onChange={handleChange}
            disabled={loading || !fleetContext || canopies.length === 0}
          >
            {loading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
                <Typography sx={{ ml: 1 }}>
                  Loading Canopies...{retryCount > 0 ? ` (Retry ${retryCount})` : ''}
                </Typography>
              </MenuItem>
            ) : !fleetContext ? (
              <MenuItem value="" disabled>Select a fleet first</MenuItem>
            ) : error ? (
              <MenuItem value="" disabled>Error loading canopies</MenuItem>
            ) : canopies.length === 0 ? (
              <MenuItem value="" disabled>No canopies found</MenuItem>
            ) : (
              canopies.map((canopy) => (
                <MenuItem key={canopy.canopyId} value={canopy.canopyId}>
                  {getCanopyDisplayName(canopy)}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        {canManage && <IconButton aria-label="Edit canopy" color="primary" disabled={!canopyContext} onClick={() => handleEditCanopy(canopyContext)}>
          <EditIcon />
        </IconButton>}
        {canManage && <IconButton aria-label="Delete canopy" color="error" disabled={!canopyContext}>
          <DeleteIcon />
        </IconButton>}
      </Stack>
      
      {error && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Typography color="error" variant="caption">
            {error}
          </Typography>
          <Button size="small" onClick={handleRetry} disabled={loading}>
            Retry
          </Button>
        </Stack>
      )}
      
      {canopyContext && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="primary">
            Selected Canopy: {getCanopyDisplayName(canopyContext)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Telemetry data will display below for this canopy
          </Typography>
        </Box>
      )}

      {canManage && isEditing && (
        <InlineEditForm
          data={editingCanopy}
          fields={canopyFields}
          onSave={handleSaveCanopy}
          onCancel={handleCancelEdit}
          isSubmitting={isSubmitting}
          title={editingCanopy?.canopyId ? "Edit Canopy" : "Create New Canopy"}
        />
      )}

      <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleNotificationClose}>
        <Alert onClose={handleNotificationClose} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CanopySelector;
